/**
 * One intent, one record (PROJECT_SPEC.md ERR-009, ERR-005).
 *
 * Every state-changing screen mints a fresh `clientCommandId` per click, so a double click, or a retry after a
 * dropped connection, would arrive at the server as two different commands. This guard sits under all of them:
 *
 *  - an identical submission made while the first is still in flight is the same submission — it shares the
 *    first one's promise and sends nothing;
 *  - when an attempt ends without a verdict (no response, or a 5xx) the person cannot know whether it was
 *    recorded, so the retry carries the ORIGINAL command id and the server answers "already recorded" instead
 *    of recording it twice;
 *  - a verdict (success, or a refusal that says nothing was written) ends the intent; the next identical
 *    submission is a new act with a new id.
 *
 * The server stays the authority (CONC-002 idempotency on `client_command_id`); this only makes sure the
 * client does not manufacture duplicates it would then depend on the server to catch.
 */
export interface Submission<T> {
  result: Promise<T>;
  /** True when this call joined an attempt already in flight and sent nothing itself. */
  joined: boolean;
}

const inflight = new Map<string, Promise<unknown>>();
const unresolved = new Map<string, string>(); // intent -> command id of an attempt with no verdict

const intentOf = (path: string, body: Record<string, unknown>): string => {
  const { clientCommandId: _ignored, ...rest } = body;
  return `${path}\n${JSON.stringify(rest)}`;
};

export function submitOnce<T>(
  path: string,
  body: Record<string, unknown>,
  send: (body: Record<string, unknown>) => Promise<T>,
  hasNoVerdict: (e: unknown) => boolean,
): Submission<T> {
  if (typeof body["clientCommandId"] !== "string") return { result: send(body), joined: false };
  const key = intentOf(path, body);
  const running = inflight.get(key);
  if (running) return { result: running as Promise<T>, joined: true };

  const id = unresolved.get(key) ?? (body["clientCommandId"] as string);
  const attempt = send({ ...body, clientCommandId: id })
    .then((r) => {
      unresolved.delete(key);
      return r;
    })
    .catch((e: unknown) => {
      if (hasNoVerdict(e)) unresolved.set(key, id);
      else unresolved.delete(key);
      throw e;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, attempt);
  return { result: attempt, joined: false };
}

/** Test seam: forget every remembered intent. */
export function resetSubmissions(): void {
  inflight.clear();
  unresolved.clear();
}
